import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface ZonaStat {
  zona: string;
  total: number;
  alDia: number;
  morosos: number;
  cortados: number;
  deudaTotal: number;
  recaudadoMes: number;
}

const EstadisticasZona: React.FC = () => {
  const [zonas, setZonas] = useState<ZonaStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [orden, setOrden] = useState<'zona' | 'total' | 'deuda'>('total');

  const cargar = async () => {
    setLoading(true);
    try {
      const [cliRes, facRes, pagRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/facturas'),
        api.get('/pagos'),
      ]);
      const clientes: any[] = cliRes.data;
      const facturas: any[] = facRes.data;
      const pagos: any[] = pagRes.data;

      const hoy = new Date();
      const mes = hoy.getMonth() + 1;
      const anio = hoy.getFullYear();

      const grupos: Record<string, ZonaStat> = {};

      clientes.forEach(c => {
        const zona = c.zona?.trim() || 'Sin zona';
        if (!grupos[zona]) {
          grupos[zona] = { zona, total: 0, alDia: 0, morosos: 0, cortados: 0, deudaTotal: 0, recaudadoMes: 0 };
        }
        const g = grupos[zona];
        g.total++;

        if (c.estado === 'cortado') {
          g.cortados++;
        } else {
          const facsVencidas = facturas.filter(f =>
            f.clienteId === c.id &&
            (f.estado === 'pendiente' || f.estado === 'vencido') &&
            new Date(f.fechaVencimiento) < hoy
          );
          const deuda = facsVencidas.reduce((s: number, f: any) => s + Number(f.monto), 0);
          g.deudaTotal += deuda;
          if (facsVencidas.length === 0) g.alDia++;
          else g.morosos++;
        }

        // Recaudado este mes
        const pagosCliente = pagos.filter(p => {
          const fp = new Date(p.createdAt);
          return p.clienteId === c.id && fp.getMonth() + 1 === mes && fp.getFullYear() === anio;
        });
        g.recaudadoMes += pagosCliente.reduce((s: number, p: any) => s + Number(p.monto), 0);
      });

      setZonas(Object.values(grupos));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const ordenado = [...zonas].sort((a, b) => {
    if (orden === 'zona') return a.zona.localeCompare(b.zona);
    if (orden === 'deuda') return b.deudaTotal - a.deudaTotal;
    return b.total - a.total;
  });

  if (loading) return <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div>;

  const totalClientes = zonas.reduce((s, z) => s + z.total, 0);
  const totalDeuda = zonas.reduce((s, z) => s + z.deudaTotal, 0);
  const totalRecaudado = zonas.reduce((s, z) => s + z.recaudadoMes, 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>Estadisticas por Zona</h2>
          <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>Distribucion de clientes y deuda por zona geografica</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {(['total','zona','deuda'] as const).map(o => (
            <button key={o} onClick={() => setOrden(o)}
              style={{ padding:'8px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                background: orden === o ? '#0ea5e9' : '#1e293b',
                color: orden === o ? 'white' : '#64748b' }}>
              {o === 'total' ? 'Por clientes' : o === 'zona' ? 'Alfabético' : 'Por deuda'}
            </button>
          ))}
          <button onClick={cargar}
            style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #334155', cursor:'pointer', fontSize:12, background:'#0f172a', color:'#94a3b8', fontWeight:600 }}>
            ↻
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:`${zonas.length} zonas`, value:`${totalClientes} clientes`, color:'#38bdf8' },
          { label:'Deuda total', value:`S/. ${totalDeuda.toFixed(2)}`, color:'#ef4444' },
          { label:'Recaudado este mes', value:`S/. ${totalRecaudado.toFixed(2)}`, color:'#22c55e' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:10, padding:'14px 18px', border:`1px solid ${s.color}33` }}>
            <div style={{ color:'#64748b', fontSize:12 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:20, fontWeight:800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {ordenado.map(z => {
          const pctAlDia = z.total > 0 ? z.alDia / z.total : 0;
          const pctMorosos = z.total > 0 ? z.morosos / z.total : 0;
          return (
            <div key={z.zona} style={{ background:'#1e293b', borderRadius:12, padding:'16px 20px', border:'1px solid #334155' }}>
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:12 }}>
                <div style={{ width:36, height:36, borderRadius:8, background:'#0ea5e922', display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#38bdf8', fontWeight:800, fontSize:14, flexShrink:0 }}>
                  {z.zona.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ color:'#f1f5f9', fontWeight:700, fontSize:15 }}>{z.zona}</div>
                  <div style={{ color:'#64748b', fontSize:12 }}>{z.total} cliente{z.total !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'#ef4444', fontWeight:700, fontSize:15 }}>
                    {z.deudaTotal > 0 ? `S/. ${z.deudaTotal.toFixed(2)}` : '—'}
                  </div>
                  <div style={{ color:'#22c55e', fontSize:12 }}>S/. {z.recaudadoMes.toFixed(2)} cobrado</div>
                </div>
              </div>

              {/* Barra de distribución */}
              <div style={{ height:8, background:'#0f172a', borderRadius:4, overflow:'hidden', marginBottom:10 }}>
                <div style={{ height:'100%', display:'flex' }}>
                  <div style={{ width:`${pctAlDia * 100}%`, background:'#22c55e', transition:'width .3s' }} />
                  <div style={{ width:`${pctMorosos * 100}%`, background:'#ef4444', transition:'width .3s' }} />
                  <div style={{ width:`${z.total > 0 ? z.cortados/z.total * 100 : 0}%`, background:'#475569', transition:'width .3s' }} />
                </div>
              </div>

              <div style={{ display:'flex', gap:12, fontSize:11 }}>
                <span style={{ color:'#22c55e' }}>● {z.alDia} al dia</span>
                <span style={{ color:'#ef4444' }}>● {z.morosos} morosos</span>
                <span style={{ color:'#64748b' }}>● {z.cortados} cortados</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EstadisticasZona;
